import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ClipboardCheck, Pencil, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/common";

const STATUSES = ["present", "absent", "excused", "late"];

export default function Training() {
  const { data: trainings, loading, refetch } = useFetch("/trainings", []);
  const { data: teams } = useFetch("/teams", []);
  const { data: players } = useFetch("/players?sort_by=name_asc", []);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [att, setAtt] = useState(null); // training for attendance
  const [attList, setAttList] = useState([]);
  const [del, setDel] = useState(null);

  const teamName = (id) => (teams || []).find((t) => t.id === id)?.name || "—";
  const teamPlayers = (teamId) => (players || []).filter((p) => !teamId || p.team_id === teamId);

  const save = async () => {
    try {
      if (f.id) await client.put(`/trainings/${f.id}`, f); else await client.post("/trainings", f);
      toast.success("Saved"); setOpen(false); setF({}); refetch();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openAtt = (tr) => {
    const map = {}; (tr.attendance || []).forEach((a) => (map[a.player_id] = a.status));
    setAttList(teamPlayers(tr.team_id).map((p) => ({ player_id: p.id, name: p.full_name, status: map[p.id] || "present" })));
    setAtt(tr);
  };
  const saveAtt = async () => {
    try { await client.put(`/trainings/${att.id}`, { attendance: attList.map(({ player_id, status }) => ({ player_id, status })) }); toast.success("Attendance saved"); setAtt(null); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => { try { await client.delete(`/trainings/${del.id}`); toast.success("Archived"); setDel(null); refetch(); } catch (e) { toast.error(apiError(e)); } };

  if (loading) return <Loading />;
  return (
    <div>
      <PageHeader title="Training" subtitle="Sessions, schedule and attendance" testid="training-page">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setF({}); setOpen(true); }} data-testid="training-add"><Plus size={16} className="me-1" /> New Session</Button>
      </PageHeader>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border"><tr>
              {["Date", "Session", "Team", "Time", "Field", "Attendance", ""].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {(trainings || []).length === 0 && <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No sessions yet</td></tr>}
              {(trainings || []).map((tr) => (
                <tr key={tr.id} className="hover:bg-muted/40" data-testid={`training-${tr.id}`}>
                  <td className="px-4 py-3">{tr.date}</td>
                  <td className="px-4 py-3 font-medium">{tr.title}</td>
                  <td className="px-4 py-3">{teamName(tr.team_id)}</td>
                  <td className="px-4 py-3">{tr.time || "—"}</td>
                  <td className="px-4 py-3">{tr.field || "—"}</td>
                  <td className="px-4 py-3">{(tr.attendance || []).length} recorded</td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => openAtt(tr)} data-testid={`att-${tr.id}`}><ClipboardCheck size={15} className="me-1" /> Attendance</Button>
                    <Button size="icon" variant="ghost" onClick={() => { setF({ ...tr }); setOpen(true); }}><Pencil size={15} /></Button>
                    <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setDel(tr)}><Trash2 size={15} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{f.id ? "Edit" : "New"} Training Session</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="mb-1.5 block">Title</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="tr-title" /></div>
            <div><Label className="mb-1.5 block">Team</Label>
              <Select value={f.team_id || ""} onValueChange={(v) => setF({ ...f, team_id: v })}>
                <SelectTrigger data-testid="tr-team"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(teams || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Field</Label><Input value={f.field || ""} onChange={(e) => setF({ ...f, field: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="tr-date" /></div>
            <div><Label className="mb-1.5 block">Time</Label><Input type="time" value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="tr-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!att} onOpenChange={(v) => !v && setAtt(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Attendance — {att?.title} ({att?.date})</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
            {attList.length === 0 && <p className="text-muted-foreground text-sm">No players in this team. Assign players to the team first.</p>}
            {attList.map((a, i) => (
              <div key={a.player_id} className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{a.name}</span>
                <div className="flex gap-1">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setAttList(attList.map((x, j) => j === i ? { ...x, status: s } : x))}
                      className={`px-2 py-1 rounded-md text-xs font-semibold capitalize border ${a.status === s ? "border-emerald-500 bg-emerald-500/10 text-emerald-700" : "border-border text-muted-foreground"}`} data-testid={`att-${a.player_id}-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAtt(null)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveAtt} disabled={!attList.length} data-testid="att-save">Save Attendance</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Archive session?" description="Move this training session to Trash." confirmText="Archive" onConfirm={doDelete} />
    </div>
  );
}
